import { useCreateUserTokenMutation } from '@app/services';
import { DatePicker, Form, Input, message, Modal } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import TokenRevealModal from './TokenRevealModal';

interface Props {
    open: boolean;
    onClose: () => void;
}

export default function CreateTokenModal({ open, onClose }: Props) {
    const [form] = Form.useForm();
    const [createToken, { isLoading }] = useCreateUserTokenMutation();
    const [token, setToken] = useState<string | null>(null);

    const handleCreate = async () => {
        const values = await form.validateFields();
        try {
            const result = await createToken({
                name: values.name.trim(),
                expiresAt: values.expiresAt?.toISOString(),
                password: values.password,
            }).unwrap();
            setToken(result.token);
            form.resetFields();
        } catch (error) {
            if ((error as { status?: number })?.status === 401) {
                form.setFields([{ name: 'password', errors: ['Password is incorrect'] }]);
            } else {
                message.error('Could not create the token. Please try again.');
            }
        }
    };

    // Don't keep the password in the form after the dialog closes.
    const handleCancel = () => {
        form.resetFields(['password']);
        onClose();
    };

    if (token) {
        return (
            <TokenRevealModal
                token={token}
                open
                onClose={() => {
                    setToken(null);
                    onClose();
                }}
            />
        );
    }

    return (
        <Modal title="Create API Token" open={open} onOk={handleCreate} onCancel={handleCancel} okText="Create" confirmLoading={isLoading}>
            <Form form={form} layout="vertical">
                <Form.Item
                    name="name"
                    label="Token Name"
                    rules={[
                        { required: true, whitespace: true, message: 'Please enter a name for this token' },
                        { max: 100, message: 'Use 100 characters or fewer' },
                    ]}
                >
                    <Input placeholder="e.g. CI pipeline" />
                </Form.Item>
                <Form.Item name="expiresAt" label="Expiration (optional)">
                    <DatePicker showTime style={{ width: '100%' }} disabledDate={d => d.isBefore(dayjs(), 'day')} />
                </Form.Item>
                <Form.Item
                    name="password"
                    label="Your password"
                    extra="Confirm it's you"
                    rules={[{ required: true, message: 'Please enter your password' }]}
                >
                    <Input.Password autoComplete="current-password" />
                </Form.Item>
            </Form>
        </Modal>
    );
}
